/*
 * Copyright (c) 2026, FusionAuth, All Rights Reserved
 */

import { Cadence, CadenceResult, createCadence } from '@rolobits/is-human-cadence';
import { logger } from '../Logger';

/**
 * Collects browser-side behavioural signals and surfaces them to the server via
 * a hidden form field (`botDetectionScore`).
 *
 * On construction the service appends a hidden `<input name="botDetectionScore">` to
 * the target element and immediately starts the cadence analyser. Any form inside
 * `target` that is submitted while the analyser is running will automatically
 * include the latest score, allowing the server to distinguish human users from
 * bots without any additional wiring.
 *
 * ### How the score reaches the server
 * The cadence library fires `onScore` callbacks as the user interacts with the
 * page. Each callback calls {@link writeHiddenInput}, which overwrites the hidden
 * input value with the latest confident score (or clears it when the classifier
 * is not yet confident). The value is therefore always fresh at form-submit time.
 */
export class SignalService {
  private cadence?: Cadence;
  private readonly hiddenInput: HTMLInputElement;

  /**
   * Creates a `SignalService` attached to `target`.
   *
   * A hidden `botDetectionScore` input is appended to `target` and the cadence
   * analyser is started immediately.
   *
   * @param target - The form element (or any ancestor) to attach the hidden input
   *                 and cadence listener to.
   */
  constructor(private target: HTMLElement) {
    this.hiddenInput = document.createElement('input');
    this.hiddenInput.type = 'hidden';
    this.hiddenInput.name = 'botDetectionScore';
    this.target.appendChild(this.hiddenInput);

    this.initCadence();
  }

  /**
   * Writes the latest cadence result into the hidden `botDetectionScore` input so it
   * is included in the next form submission.
   *
   * Writes the numeric score as a string when the classifier is confident, otherwise
   * clears the field so the server treats the signal as unknown (omitted).
   */
  private writeHiddenInput(result: { confident: boolean; score: number }) {
    let value = '';
    if (result.confident) {
      // invert the score from humanness to bot-ness
      const normalized = (1 - result.score).toFixed(2);
      value = `${normalized}`;
    }
    this.hiddenInput.value = value;
  }

  /**
   * Initializes the cadence analyzer on the target element.
   *
   * The analyzer observes keystroke and pointer timing patterns and calls `onScore`
   * whenever it produces a new result.
   */
  private initCadence() {
    const onScore = (result: CadenceResult) => {
      logger.logEvery(3000, 'cadence', 'cadence result', result);
      this.writeHiddenInput(result);
    }

    this.cadence = createCadence(this.target, {onScore});

    this.cadence.start();
  }
}

/**
 * Convenience factory that creates a {@link SignalService} for the given element.
 *
 * Prefer this over constructing `SignalService` directly unless you need to hold
 * a reference to the service instance.
 *
 * @param target - The element to attach signals to (typically the login form).
 */
export const addSignals = (target: HTMLElement) => {
  new SignalService(target);
}
